import { useEffect, useState } from "preact/hooks";
import { createPortal } from "preact/compat";
import { usePortalContainer } from "@/context/PortalContext";
import { cn } from "@/lib/utils";
import {
  X,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
} from "lucide-react";

interface Toast {
  id: string;
  message: string;
  color: "success" | "danger" | "warning" | "default";
  duration?: number;
}

let toasts: Toast[] = [];
let listeners: Array<(toasts: Toast[]) => void> = [];

export function addToast(toast: Omit<Toast, "id">): void {
  const id = Math.random().toString(36).substring(7);
  const newToast = { ...toast, id, duration: toast.duration || 5000 };

  toasts = [...toasts, newToast];
  listeners.forEach((listener) => listener(toasts));

  // Auto-remove after duration
  setTimeout(() => {
    removeToast(id);
  }, newToast.duration);
}

function removeToast(id: string): void {
  toasts = toasts.filter((t) => t.id !== id);
  listeners.forEach((listener) => listener(toasts));
}

export function ToastContainer() {
  const [currentToasts, setCurrentToasts] = useState<Toast[]>([]);
  const portalContainer = usePortalContainer();

  useEffect(() => {
    const listener = (newToasts: Toast[]) => {
      setCurrentToasts(newToasts);
    };
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  if (!portalContainer || currentToasts.length === 0) {
    return null;
  }

  return createPortal(
    <div className="tw:fixed tw:bottom-4 tw:left-1/2 tw:-translate-x-1/2 tw:z-9999 tw:flex tw:flex-col-reverse tw:gap-2 tw:pointer-events-none tw:w-[calc(100%-1rem)] tw:max-w-[420px]">
      {currentToasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>,
    portalContainer
  );
}

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

function ToastItem({ toast, onClose }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 200);
  };

  const toastConfig = {
    success: {
      icon: CheckCircle2,
      bgClass: "tw:bg-white tw:border-l-4 tw:border-green-500",
      iconClass: "tw:text-green-500",
      textClass: "tw:text-gray-900",
    },
    danger: {
      icon: AlertCircle,
      bgClass: "tw:bg-white tw:border-l-4 tw:border-red-500",
      iconClass: "tw:text-red-500",
      textClass: "tw:text-gray-900",
    },
    warning: {
      icon: AlertTriangle,
      bgClass: "tw:bg-white tw:border-l-4 tw:border-yellow-500",
      iconClass: "tw:text-yellow-500",
      textClass: "tw:text-gray-900",
    },
    default: {
      icon: Info,
      bgClass: "tw:bg-white tw:border-l-4 tw:border-blue-500",
      iconClass: "tw:text-blue-500",
      textClass: "tw:text-gray-900",
    },
  };

  const config = toastConfig[toast.color];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "tw:pointer-events-auto tw:w-full tw:rounded-lg tw:shadow-xl tw:p-4 tw:transition-all tw:duration-300 tw:ease-out",
        config.bgClass,
        isExiting
          ? "tw:opacity-0 tw:translate-y-2 tw:scale-95"
          : "tw:opacity-100 tw:translate-y-0 tw:scale-100"
      )}
      style={{
        boxShadow:
          "0 10px 40px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)",
      }}
    >
      <div className="tw:flex tw:items-start tw:gap-3">
        <div className={cn("tw:shrink-0 tw:mt-0.5", config.iconClass)}>
          <Icon size={20} strokeWidth={2} />
        </div>
        <div className="tw:flex-1 tw:min-w-0">
          <p
            className={cn(
              "tw:font-medium tw:text-sm tw:leading-snug",
              config.textClass
            )}
          >
            {toast.message}
          </p>
        </div>
        <button
          onClick={handleClose}
          className="tw:shrink-0 tw:text-gray-400 hover:tw:text-gray-600 tw:transition-colors tw:rounded-full tw:p-1 hover:tw:bg-gray-100"
          aria-label="Close"
        >
          <X size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
